import type { MedicationPadItemType } from '@/components/Patient/components/PrescriptionPad/PrescriptionPad.types';
import type { ProductFormulation } from '@/constants/products';
import { calculateDispenseAmount, sumDispenseAmounts } from './calculateDispenseAmount';
import type { DosageUnit } from './constants';
import { DISPENSE_LIMIT_FORMULATIONS, UNIT_BY_FORMULATION } from './constants';
import type { DispenseLimitsByFormulation, DispenseLimitStatus } from './dispenseLimit';
import { getDispenseLimitStatus, isValidDispenseLimit } from './dispenseLimit';
import { formulationHasUnreadableNetWeight } from './formulationHasUnreadableNetWeight';
import { getFormulationFilter } from './getFormulationFilter';

export type DispenseLimitRow = {
  formulation: ProductFormulation;
  unit?: DosageUnit;
  /** Pad items of the formulation with a product chosen, whether or not each has a dispense amount */
  productCount: number;
  /** Dispense amount over 30 days summed across the formulation's pad items */
  prescribed: number;
  /** The limit in force: the prescriber's set limit when it is valid, else the amount prescribed */
  limit: number;
  /** What the prescriber typed, kept apart from `limit` so an unreadable value can be flagged; undefined when none set */
  setLimit?: number;
  isConfirmed: boolean;
  status: DispenseLimitStatus;
};

/** A limit below the amount prescribed that the prescriber has not acknowledged yet */
export const isPendingDispenseLimit = ({ status, isConfirmed }: Pick<DispenseLimitRow, 'status' | 'isConfirmed'>) =>
  status === 'over' && !isConfirmed;

/**
 * One row per formulation that supports a dispense limit and has at least one pad item with a dispense amount,
 * in the order the formulations are configured. None for a formulation with a product whose net weight is missing or
 * unreadable.
 * @param formulations - only these formulations' rows, when the caller needs no others
 */
export const getFormulationDispenseLimitRows = (
  medicationPadItems: MedicationPadItemType[],
  dispenseLimits: DispenseLimitsByFormulation,
  formulations: ProductFormulation[] = [...DISPENSE_LIMIT_FORMULATIONS],
): DispenseLimitRow[] =>
  formulations
    .filter((formulation) => DISPENSE_LIMIT_FORMULATIONS.has(formulation))
    .flatMap((formulation) => {
      // A total without that product's amount would understate the pad, so the formulation goes without dispense limits,
      // as it does without dispense amounts, rather than apply them to bad product data or block the prescription
      if (formulationHasUnreadableNetWeight(medicationPadItems, formulation)) {
        return [];
      }

      const padItems = medicationPadItems.filter(
        (item) => item.medicationPadItem !== undefined && getFormulationFilter(item.filters) === formulation,
      );
      const amounts = padItems
        .map(({ medicationPadItem }) => calculateDispenseAmount(medicationPadItem))
        .filter((amount) => amount !== undefined);

      if (!amounts.length) {
        return [];
      }

      const prescribed = sumDispenseAmounts(amounts);
      const setLimit = dispenseLimits[formulation]?.limit;
      const limit = setLimit !== undefined && isValidDispenseLimit(setLimit) ? setLimit : prescribed;

      return [
        {
          formulation,
          unit: UNIT_BY_FORMULATION.get(formulation),
          productCount: padItems.length,
          prescribed,
          limit,
          setLimit,
          isConfirmed: dispenseLimits[formulation]?.isConfirmed ?? false,
          status: getDispenseLimitStatus({ prescribed, limit }),
        },
      ];
    });
